import { useCallback, useState } from 'react';
import { useDocumentAttachment } from '~/services/document';
import { downloadUrl } from '~/utils/download';
import { isSupportedXmlDocumentUrl, loadXmlDocument } from '~/utils/xml-parser';
import useExceptionLogger from './useExceptionLogger';

const useInvoiceXml = (invoiceId: string) => {
  const { captureException } = useExceptionLogger();
  const { mutateAsync: getAttachment, status: attachmentStatus } = useDocumentAttachment(invoiceId);
  const [isLoadingXml, setIsLoadingXml] = useState(true);
  const [xmlDocument, setXmlDocument] = useState<Document>();
  const [hasDownloadedAttachment, setHasDownloadedAttachment] = useState(false);

  const loadXml = useCallback(async () => {
    setIsLoadingXml(true);
    setHasDownloadedAttachment(false);
    try {
      const url = await getAttachment();
      if (!isSupportedXmlDocumentUrl(url)) {
        downloadUrl(url);
        setHasDownloadedAttachment(true);
        setXmlDocument(undefined);
        return;
      }

      const xmlDocument = await loadXmlDocument(url);
      setXmlDocument(xmlDocument);
    } catch (err) {
      captureException(err);
    } finally {
      setIsLoadingXml(false);
    }
  }, [getAttachment, captureException, setXmlDocument, setIsLoadingXml]);

  return {
    loadXml,
    isLoading: isLoadingXml,
    hasDownloadedAttachment,
    xmlDocument,
    loadingStatus: attachmentStatus,
  };
};

export default useInvoiceXml;
